/** The bookkeeping of a request sent a while after the last change it
 * reads, whose body is skipped when it is the one last sent, and whose
 * answer applies only while it is the latest one started.
 *
 * `begin` numbers each request; an answer is applied when `isLatest` says
 * its number is still the latest, and `finish` marks it back. */
export class DebouncedRequest {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private seq = 0;
  // The body last begun, as JSON; `null` when none was. A failed body is
  // kept too: it is sent again only after `forget`.
  private sent: string | null = null;
  private inFlightFlag = false;

  /** `fire` is called `delayMs` after the last `schedule`. */
  constructor(private readonly delayMs: number, private readonly fire: () => void) {}

  /** Whether a request is on its way. */
  get inFlight(): boolean {
    return this.inFlightFlag;
  }

  /** Whether a request is waiting to be sent or on its way: an answer
   * shown may be out of date. */
  get pending(): boolean {
    return this.timer !== null || this.inFlightFlag;
  }

  /** (Re)starts the wait before `fire` is called. `body`, when given,
   * returns the body `fire` would begin now, as JSON: while no wait runs,
   * none starts for a body `begin` would skip. */
  schedule(body?: () => string | null): void {
    if (this.timer === null && body && this.isSent(body())) {
      return;
    }
    this.cancel();
    this.timer = setTimeout(() => {
      this.timer = null;
      this.fire();
    }, this.delayMs);
  }

  /** Stops a wait; a request on its way is left as it is. */
  cancel(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /** Starts a request with `body`, as JSON, and returns its number; `null`,
   * starting nothing, when `body` is the one last begun. A `null` body
   * starts no request but still supersedes the one on its way. */
  begin(body: string | null): number | null {
    if (this.isSent(body)) {
      return null;
    }
    this.sent = body;
    this.inFlightFlag = body !== null;
    return ++this.seq;
  }

  /** Whether `body` is the one last begun. */
  private isSent(body: string | null): boolean {
    return body !== null && body === this.sent;
  }

  /** Whether `seq` is the latest request begun, not superseded since. */
  isLatest(seq: number): boolean {
    return seq === this.seq;
  }

  /** Marks request `seq` as back, when it is the latest. */
  finish(seq: number): void {
    if (this.isLatest(seq)) {
      this.inFlightFlag = false;
    }
  }

  /** Forgets the body last begun, so the same body is sent again. */
  forget(): void {
    this.sent = null;
  }

  /** Supersedes a request on its way and forgets the body last begun. A
   * wait is left as it is (`cancel`). */
  reset(): void {
    this.seq++;
    this.sent = null;
    this.inFlightFlag = false;
  }
}
